'use client';
import './sort.css';
import { useState, useEffect, useRef, useMemo, useCallback, Fragment } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { ActionIcon, Button, Text, Box, Collapse, Group, Popover, Loader, Center, SegmentedControl } from '@mantine/core';
import { IconX, IconCheck, IconChevronDown, IconArrowLeft, IconArrowRight, IconMoneybag, IconSearch, IconClipboardList, IconRefresh } from '@tabler/icons-react';
import { useSortProgress } from '@/hooks/useSortProgress';
import { usePartOrder } from '@/hooks/usePartOrder';
import { groupPartsByType } from '@/lib/partTypes';
import { SortPartTile } from '@/components/sort/SortPartTile';
import { BagPickerSheet } from '@/components/sort/BagPickerSheet';
import { SortSearchSheet } from '@/components/sort/SortSearchSheet';
import { MissingPartsSheet } from '@/components/sort/MissingPartsSheet';
import api from '@/lib/api';
import { getImageUrl } from '@/lib/imageUrl';
import { trackEvent } from '@/lib/telemetry';
import { isSortModeNew } from '@/lib/sortModeLaunch';

// Kort label voor knoppen/chips: "3", "3+4" (display numbers) of "Extra"
function shortBagLabel(bag) {
  if (bag.leftover) return 'Left over';
  if (bag.displayNumbers && bag.displayNumbers.length > 0) return bag.displayNumbers.join('+');
  if (bag.bagNumber === 'Extra parts' || bag.bagNumber === 'Extra') return 'Extra';
  return bag.bagNumber;
}

// Volledige kop: "Bag 3", "Group 3", "Left over" of "Loose parts"
function fullBagLabel(bag) {
  if (bag.leftover) return 'Left over';
  const short = shortBagLabel(bag);
  if (short === 'Extra') return 'Loose parts';
  if (isNaN(parseInt(short, 10))) return short;
  return `${bag.virtual ? 'Group' : 'Bag'} ${short}`;
}

// Label voor een alternatieve build: eigen naam of "Build 2"/"Build 3"
// (hoofdbuild = build 1 = de complete set; union-sets hebben geen hoofdbuild
// en nummeren vanaf 1)
function buildLabel(build, index, hasMainBuild = true) {
  return build.name || `Build ${index + (hasMainBuild ? 2 : 1)}`;
}

export default function SortClient({ initialSet }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const id = initialSet.id;

  // Multi-build: ?build=sm02 sorteert die build (groups), ?build=complete de
  // hele set; zonder param op een multi-build set verschijnt eerst de kiezer
  const builds = initialSet.builds || [];
  const hasMainBuild = (initialSet.build_count || 0) > builds.length;
  const buildParam = searchParams.get('build');
  const activeBuildIdx = builds.findIndex(b => b.code === buildParam);
  const wantsBuild = activeBuildIdx >= 0;
  const showPicker = builds.length > 0 && !buildParam;

  const [buildSet, setBuildSet] = useState(null);
  const [buildError, setBuildError] = useState(null);
  const [buildRetry, setBuildRetry] = useState(0);
  useEffect(() => {
    if (!wantsBuild) { setBuildSet(null); setBuildError(null); return; }
    let alive = true;
    setBuildError(null);
    api.fetchSetById(id, false, buildParam)
      .then(data => { if (alive) setBuildSet(data); })
      .catch(err => { if (alive) setBuildError(err); });
    return () => { alive = false; };
  }, [wantsBuild, buildParam, id, buildRetry]);

  const set = wantsBuild ? buildSet : initialSet;
  // Terug naar de setpagina mét dezelfde build-weergave
  const exitUrl = `/set/${id}${wantsBuild ? `?build=${buildParam}` : ''}`;

  // Voortgang per kiezer-optie, uit de gedeelde localStorage-map (in een
  // effect: SSR kent localStorage niet en de balkjes zijn puur client-side)
  const [pickerProgress, setPickerProgress] = useState(null);
  useEffect(() => {
    if (!showPicker) return;
    try {
      const stored = JSON.parse(localStorage.getItem(`bricksperbag_check_${id}`) || '{}');
      const sumFor = (bagIds) => {
        let checked = 0;
        for (const [key, v] of Object.entries(stored)) {
          if (bagIds.has(key.split('_')[0])) checked += typeof v === 'number' ? v : 0;
        }
        return checked;
      };
      const completeBags = (initialSet.bags || []).filter(b => b.bagNumber !== '0');
      const completeTotal = completeBags.reduce((t, b) =>
        t + (b.parts || []).reduce((s, p) => s + (p.quantity || 0), 0), 0);
      const progress = {
        complete: { checked: sumFor(new Set(completeBags.map(b => String(b.id)))), total: completeTotal },
      };
      for (const b of builds) {
        progress[b.code] = {
          checked: sumFor(new Set((b.bagIds || []).map(String))),
          total: b.totalQuantity || 0,
        };
      }
      setPickerProgress(progress);
    } catch (e) { setPickerProgress(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPicker, id]);

  const {
    checkedParts,
    totalCheckProgress,
    bagCompletion,
    handleToggleCheck,
    handleResetBag,
    handleResetAll,
  } = useSortProgress(id, set, { active: true });

  const bags = useMemo(() => ((set && set.bags) || []).filter(bag => {
    if (bag.bagNumber === '0') return false;
    if (bag.bagNumber === 'Extra parts' && (!bag.parts || bag.parts.length === 0)) return false;
    return true;
  }), [set]);

  // Startzak: de eerste niet-voltooide zak volgens de ópgeslagen voortgang.
  // Direct uit localStorage gelezen - de hook laadt dezelfde data pas in een
  // effect, en dan zou dit initiële effect nog de lege state zien.
  const [activeIndex, setActiveIndex] = useState(0);
  const didInitBag = useRef(false);
  // Bij het wisselen van build (zelfde route, geen remount) opnieuw bij de
  // eerste onvoltooide group beginnen - een oude index kan buiten bereik zijn
  useEffect(() => {
    didInitBag.current = false;
    setActiveIndex(0);
  }, [buildParam]);
  useEffect(() => {
    if (didInitBag.current || bags.length === 0) return;
    didInitBag.current = true;
    trackEvent('Check Started', { set: id, source: 'sort' });
    try {
      const stored = JSON.parse(localStorage.getItem(`bricksperbag_check_${id}`) || '{}');
      // Left over overslaan: die is optioneel - als alleen die nog open staat
      // hoort de gebruiker op het voltooiingsscherm te landen
      const idx = bags.findIndex(bag => !bag.leftover && (bag.parts || []).some(part => {
        const key = `${bag.id}_${part.partId}_${part.color_id || 'nocolor'}`;
        const v = stored[key];
        const count = typeof v === 'number' ? v : (v ? (part.quantity || 0) : 0);
        return count < (part.quantity || 0);
      }));
      if (idx > 0) setActiveIndex(idx);
    } catch (e) { /* corrupt state → gewoon bij zak 1 beginnen */ }
  }, [id, bags]);

  const activeBag = bags[activeIndex] || null;

  // Wake lock: builders leave the phone next to the build for minutes between
  // taps - keep the screen on while sort mode is open. The browser drops the
  // lock whenever the tab is hidden, so re-acquire on return.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;
    let lock = null;
    let stopped = false;
    const acquire = async () => {
      try {
        const next = await navigator.wakeLock.request('screen');
        if (stopped) { next.release().catch(() => {}); return; }
        lock = next;
      } catch (e) { /* denied (e.g. battery saver) - screen dimming is acceptable */ }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') acquire();
    };
    acquire();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', onVisibility);
      if (lock) lock.release().catch(() => {});
    };
  }, []);

  // De sort mode ligt als fixed layer over de site - de pagina eronder mag
  // niet meescrollen
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmBagResetOpen, setConfirmBagResetOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [foundOpen, setFoundOpen] = useState(false);

  // Eenmalige intro voor de vernieuwde Sort mode: vooral bestaande gebruikers
  // (die de oude in-page Check Mode kenden) landen hier via een redirect in
  // een compleet nieuw scherm - één regel context, daarna nooit meer.
  // In een effect (niet in de useState-initializer): SSR kent localStorage niet.
  const [showIntro, setShowIntro] = useState(false);
  useEffect(() => {
    if (isSortModeNew() && !localStorage.getItem('bpb_sort_intro_seen')) setShowIntro(true);
  }, []);
  const dismissIntro = () => {
    setShowIntro(false);
    try { localStorage.setItem('bpb_sort_intro_seen', '1'); } catch (e) { /* localStorage unavailable */ }
  };
  const stageRef = useRef(null);

  const goToBag = useCallback((index) => {
    setActiveIndex(index);
    setFoundOpen(false);
    setConfirmBagResetOpen(false);
    stageRef.current?.scrollTo({ top: 0 });
  }, []);

  // Vanuit een zoekresultaat: naar de zak, tegel in beeld scrollen en even
  // laten oplichten (zelfde animatie als op de overzichtspagina). Als het
  // onderdeel al volledig gevonden is zit de tegel in de ingeklapte
  // Found-sectie - die klapt dan eerst open.
  const [highlightedPartKey, setHighlightedPartKey] = useState(null);
  const highlightTimerRef = useRef(null);
  const highlightStartRef = useRef(null);
  const jumpToPart = useCallback((bagIndex, partKey) => {
    setSearchOpen(false);
    setActiveIndex(bagIndex);
    const bag = bags[bagIndex];
    const bagPart = (bag?.parts || []).find(p => `${p.partId}_${p.color_id || 'nocolor'}` === partKey);
    const v = bagPart ? checkedParts[`${bag.id}_${bagPart.partId}_${bagPart.color_id || 'nocolor'}`] : undefined;
    const count = typeof v === 'number' ? v : (v ? (bagPart?.quantity || 0) : 0);
    const inFound = bagPart && count >= (bagPart.quantity || 0);
    setFoundOpen(!!inFound);
    clearTimeout(highlightTimerRef.current);
    clearTimeout(highlightStartRef.current);
    setHighlightedPartKey(null);
    highlightStartRef.current = setTimeout(() => setHighlightedPartKey(partKey), 30);
    highlightTimerRef.current = setTimeout(() => setHighlightedPartKey(null), 3600);
    // Wacht tot de zak (en evt. de Found-collapse) gerenderd is en scroll dan
    // de tegel naar het midden van het scherm
    setTimeout(() => {
      const el = stageRef.current?.querySelector(`[data-part-key="${partKey}"]`);
      if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, inFound ? 400 : 150);
  }, [bags, checkedParts]);
  useEffect(() => () => { clearTimeout(highlightTimerRef.current); clearTimeout(highlightStartRef.current); }, []);

  // Sneltoetsen (niet vanuit invoervelden): pijltjes = vorige/volgende zak,
  // f of / = zoeker openen
  useEffect(() => {
    const onKey = (e) => {
      if (e.target && /INPUT|TEXTAREA|SELECT/.test(e.target.tagName)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'ArrowLeft' && activeIndex > 0) goToBag(activeIndex - 1);
      if (e.key === 'ArrowRight' && activeIndex < bags.length - 1) goToBag(activeIndex + 1);
      if (e.key === 'f' || e.key === '/') {
        e.preventDefault();
        setSearchOpen(true);
        trackEvent('Sort Search Opened', { set: id, source: 'shortcut' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeIndex, bags.length, goToBag, id]);

  // Zelfde voorkeur als de setpagina: per kleur of per part type
  const [partOrder, changePartOrder] = usePartOrder('sort');

  // Splitsing van de actieve zak in nog-te-vinden / gevonden (zelfde regels
  // als de overzichtspagina: deels getelde parts staan in beide lijsten)
  const { uncheckedParts, checkedList, fullyFoundCount, bagChecked, bagTotal } = useMemo(() => {
    if (!activeBag?.parts) return { uncheckedParts: [], checkedList: [], fullyFoundCount: 0, bagChecked: 0, bagTotal: 0 };
    const unchecked = [];
    const checked = [];
    let fullyFound = 0;
    let piecesChecked = 0;
    let piecesTotal = 0;
    activeBag.parts.forEach(part => {
      const key = `${activeBag.id}_${part.partId}_${part.color_id || 'nocolor'}`;
      const v = checkedParts[key];
      const count = typeof v === 'number' ? v : (v ? part.quantity : 0);
      piecesTotal += part.quantity || 0;
      piecesChecked += Math.min(count, part.quantity || 0);
      if (count >= part.quantity) {
        checked.push(part);
        fullyFound += 1;
      } else if (count > 0) {
        unchecked.push(part);
        checked.push(part);
      } else {
        unchecked.push(part);
      }
    });
    return { uncheckedParts: unchecked, checkedList: checked, fullyFoundCount: fullyFound, bagChecked: piecesChecked, bagTotal: piecesTotal };
  }, [activeBag, checkedParts]);

  // Per part type: nog-te-vinden krijgt kopjes per groep; de gevonden-lijst
  // volgt dezelfde volgorde zonder kopjes (is alleen ter controle)
  const uncheckedGroups = useMemo(
    () => (partOrder === 'type' ? groupPartsByType(uncheckedParts) : null),
    [partOrder, uncheckedParts]
  );
  const foundParts = useMemo(
    () => (partOrder === 'type' ? groupPartsByType(checkedList).flatMap(g => g.parts) : checkedList),
    [partOrder, checkedList]
  );

  const getCount = useCallback((bag, part) => {
    const key = `${bag.id}_${part.partId}_${part.color_id || 'nocolor'}`;
    const v = checkedParts[key];
    return typeof v === 'number' ? v : (v ? part.quantity : 0);
  }, [checkedParts]);

  // "Continue with bag N": eerstvolgende niet-voltooide zak, met wrap-around.
  // De left over-stapel wordt nooit voorgesteld: die is optioneel (het
  // voltooiingsscherm biedt hem apart aan)
  const nextUnfinishedIndex = useMemo(() => {
    for (let step = 1; step < bags.length; step++) {
      const idx = (activeIndex + step) % bags.length;
      const bag = bags[idx];
      if (bag.leftover) continue;
      if (!bag.parts || bag.parts.length === 0) continue;
      if ((bagCompletion[bag.bagNumber] || 0) < 100) return idx;
    }
    return null;
  }, [bags, activeIndex, bagCompletion]);

  const leftoverIndex = useMemo(() => bags.findIndex(b => b.leftover), [bags]);
  const leftoverDone = leftoverIndex >= 0
    && (bagCompletion[bags[leftoverIndex].bagNumber] || 0) >= 100;
  const setComplete = totalCheckProgress.total > 0 && totalCheckProgress.percentage >= 100;
  const bagComplete = activeBag && bagTotal > 0 && bagChecked >= bagTotal;
  const prevBag = activeIndex > 0 ? bags[activeIndex - 1] : null;
  const nextBag = activeIndex < bags.length - 1 ? bags[activeIndex + 1] : null;

  // Multi-build kiezer: eerst kiezen wat je gaat bouwen
  if (showPicker) {
    const completeBags = (initialSet.bags || []).filter(b => b.bagNumber !== '0');
    const completeTotal = pickerProgress?.complete?.total
      ?? completeBags.reduce((t, b) => t + (b.parts || []).reduce((s, p) => s + (p.quantity || 0), 0), 0);
    const options = [
      {
        value: 'complete',
        label: hasMainBuild
          ? `${initialSet.main_build_name || initialSet.name} (main build)`
          : 'All bags',
        // Altijd het gewone setplaatje (zoals overal op de site); de
        // build-render is alleen vangnet voor sets zonder setfoto
        image: initialSet.image_url || initialSet.main_build_image,
        imageFallback: initialSet.main_build_image,
        sub: `${completeTotal.toLocaleString('en-US')} pieces · ${completeBags.length} ${completeBags.every(b => b.virtual) ? 'groups' : 'bags'}`,
        progress: pickerProgress?.complete,
      },
      ...builds.map((b, i) => ({
        value: b.code, label: buildLabel(b, i, hasMainBuild), image: b.imageUrl,
        sub: `${(b.pieces || 0).toLocaleString('en-US')} pieces · ${b.groupCount} groups`,
        progress: pickerProgress?.[b.code],
      })),
    ];
    const anyImage = options.some(o => o.image);
    return (
      <div className="sort-root">
        <header className="sort-top">
          <ActionIcon variant="light" color="gray" radius="xl" size="lg" onClick={() => router.push(`/set/${id}`)} aria-label="Exit sort mode">
            <IconX size={18} />
          </ActionIcon>
          <div className="sort-title">{id} · {initialSet.name}</div>
        </header>
        <main className="sort-stage">
          <Box mt="md" mb="lg">
            <Text fw={800} size="xl">What are you building?</Text>
            <Text c="dimmed" size="sm" mt={2}>
              Pick a build to sort only the parts it needs, in build order. Leftover parts go on a separate pile at the end.
            </Text>
          </Box>
          <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
            {options.map(opt => {
              const pct = opt.progress && opt.progress.total > 0
                ? Math.min(100, (opt.progress.checked / opt.progress.total) * 100)
                : 0;
              return (
                <Box
                  key={opt.value}
                  role="button" tabIndex={0}
                  onClick={() => router.replace(`/set/${id}/sort?build=${opt.value}`, { scroll: false })}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); router.replace(`/set/${id}/sort?build=${opt.value}`, { scroll: false }); } }}
                  style={{
                    border: '1.5px solid #e9ecef', borderRadius: 14, padding: '16px 18px',
                    cursor: 'pointer', backgroundColor: '#fff',
                  }}
                >
                  {anyImage && (
                    <Box style={{ height: 96, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                      {opt.image ? (
                        <img
                          src={getImageUrl(opt.image)} alt=""
                          style={{ maxHeight: 96, maxWidth: '100%', objectFit: 'contain' }}
                          onError={(e) => {
                            const fallback = opt.imageFallback && opt.imageFallback !== opt.image
                              ? getImageUrl(opt.imageFallback) : null;
                            if (fallback && e.currentTarget.src !== fallback) {
                              e.currentTarget.src = fallback;
                            } else {
                              e.currentTarget.style.visibility = 'hidden';
                            }
                          }}
                        />
                      ) : null}
                    </Box>
                  )}
                  <Text fw={700}>{opt.label}</Text>
                  <Text size="sm" c="dimmed" mt={2}>{opt.sub}</Text>
                  <Box mt={10} style={{ height: 5, borderRadius: 3, backgroundColor: '#e9ecef', overflow: 'hidden' }}>
                    <Box style={{ height: '100%', width: `${pct}%`, backgroundColor: 'var(--sort-good, #2f9e44)' }} />
                  </Box>
                  {pct > 0 && (
                    <Text size="xs" c="dimmed" mt={4}>{Math.max(1, Math.floor(pct))}% sorted</Text>
                  )}
                </Box>
              );
            })}
          </Box>
        </main>
      </div>
    );
  }

  // Build-groups worden client-side geladen
  if (wantsBuild && !set) {
    return (
      <div className="sort-root">
        <header className="sort-top">
          <ActionIcon variant="light" color="gray" radius="xl" size="lg" onClick={() => router.push(`/set/${id}`)} aria-label="Exit sort mode">
            <IconX size={18} />
          </ActionIcon>
          <div className="sort-title">{id} · {initialSet.name}</div>
        </header>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {buildError ? (
            <Box style={{ textAlign: 'center' }}>
              <Text c="dimmed" mb="sm">Couldn&apos;t load this build.</Text>
              <Button variant="light" onClick={() => setBuildRetry(k => k + 1)}>Try again</Button>
            </Box>
          ) : (
            <Center><Loader /></Center>
          )}
        </div>
      </div>
    );
  }

  if (!activeBag) {
    return (
      <div className="sort-root">
        <div className="sort-top">
          <ActionIcon variant="light" color="gray" radius="xl" size="lg" onClick={() => router.push(exitUrl)} aria-label="Exit sort mode">
            <IconX size={18} />
          </ActionIcon>
          <div className="sort-title">{id} · {set.name}</div>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Text c="dimmed">No bags found for this set.</Text>
        </div>
      </div>
    );
  }

  return (
    <div className="sort-root">
      <header className="sort-top">
        <ActionIcon variant="light" color="gray" radius="xl" size="lg" onClick={() => router.push(exitUrl)} aria-label="Exit sort mode">
          <IconX size={18} />
        </ActionIcon>
        <div className="sort-title">
          {id} · {set.name}{wantsBuild ? ` · ${buildLabel(builds[activeBuildIdx], activeBuildIdx, hasMainBuild)}` : ''}
        </div>
        <Group gap={6} wrap="nowrap" style={{ marginLeft: 'auto' }}>
          <ActionIcon
            variant="light" color="gray" radius="xl" size="lg"
            onClick={() => { setSearchOpen(true); trackEvent('Sort Search Opened', { set: id }); }}
            aria-label="Find a part"
          >
            <IconSearch size={18} />
          </ActionIcon>
          {totalCheckProgress.checked > 0 && (
            <ActionIcon
              variant="light" color="gray" radius="xl" size="lg"
              onClick={() => { setReportOpen(true); trackEvent('Check Report Opened', { set: id }); }}
              aria-label="Missing parts report"
            >
              <IconClipboardList size={18} />
            </ActionIcon>
          )}
        </Group>
      </header>

      <div className="sort-progresswrap">
        <div className="sort-segments" role="img" aria-label={`Overall progress: ${Math.floor(totalCheckProgress.percentage)} percent`}>
          {bags.map((bag, i) => {
            const pct = bagCompletion[bag.bagNumber] || 0;
            return (
              <div key={bag.bagNumber} className={`sort-segment${pct >= 100 ? ' done' : ''}${i === activeIndex ? ' active' : ''}${bag.leftover ? ' leftover' : ''}`}>
                {pct > 0 && <i style={{ width: `${Math.min(100, pct)}%` }} />}
              </div>
            );
          })}
        </div>
        <div className="sort-progresslabel">
          <span>{totalCheckProgress.checked.toLocaleString('en-US')} / {totalCheckProgress.total.toLocaleString('en-US')} pieces</span>
          <span style={{ fontWeight: 700, color: setComplete ? 'var(--sort-good)' : 'var(--sort-accent)' }}>
            {Math.floor(totalCheckProgress.percentage)}%
          </span>
        </div>
      </div>

      <main className="sort-stage" ref={stageRef}>
        {showIntro && (
          <Box p="md" mb="md" style={{ backgroundColor: '#e7f5ff', border: '1px solid #a5d8ff', borderRadius: 12 }}>
            <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
              <Box style={{ minWidth: 0 }}>
                <Text fw={700} size="sm" style={{ color: '#1971c2' }}>Sort mode is all new</Text>
                <Text size="sm" c="dimmed" mt={2}>
                  Full screen and distraction-free. Tap a part to count it, long-press to check it off at once,
                  find any part with search (<IconSearch size={13} style={{ verticalAlign: 'middle' }} />), and jump between bags below.
                </Text>
              </Box>
              <Button size="compact-xs" variant="light" onClick={dismissIntro} style={{ flexShrink: 0 }}>
                Got it
              </Button>
            </Group>
          </Box>
        )}
        {setComplete && !activeBag?.leftover ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, minHeight: '60%', textAlign: 'center', padding: '40px 20px' }} aria-live="polite">
            <Box style={{ width: 64, height: 64, borderRadius: '50%', backgroundColor: 'var(--sort-good-soft)', color: 'var(--sort-good)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconCheck size={34} stroke={3} />
            </Box>
            <Text fw={700} size="xl">{wantsBuild ? 'Build sorted!' : 'Set complete!'}</Text>
            <Text c="dimmed" size="sm">All {totalCheckProgress.total.toLocaleString('en-US')} pieces found.</Text>
            <Button mt="sm" radius="xl" color="green" onClick={() => router.push(exitUrl)}>
              Back to overview
            </Button>
            {leftoverIndex >= 0 && !leftoverDone && (
              <>
                <Button variant="light" color="gray" radius="xl" onClick={() => goToBag(leftoverIndex)}>
                  Check the left over pile (optional)
                </Button>
                <Text c="dimmed" size="xs" style={{ maxWidth: 300 }}>
                  Compare your remaining pile with the left over list to make sure no pieces are missing.
                </Text>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="sort-baghead">
              <h1>{fullBagLabel(activeBag)}</h1>
              <Text c="dimmed" size="sm">{bagChecked} / {bagTotal} pieces</Text>
              {bagChecked > 0 && (
                <Popover width={230} position="bottom-start" withArrow shadow="md" zIndex={300} opened={confirmBagResetOpen} onChange={setConfirmBagResetOpen}>
                  <Popover.Target>
                    <ActionIcon
                      variant="subtle" color="gray" radius="xl" size="sm"
                      aria-label={`Reset ${fullBagLabel(activeBag)}`}
                      onClick={() => setConfirmBagResetOpen(o => !o)}
                      // De kop lijnt uit op tekst-baseline; een icoon-knop heeft
                      // geen baseline en schiet omhoog - centreren + 2px optische
                      // correctie richting de tellertekst
                      style={{ alignSelf: 'center', marginTop: 2 }}
                    >
                      <IconRefresh size={15} />
                    </ActionIcon>
                  </Popover.Target>
                  <Popover.Dropdown>
                    <Text size="sm" fw={600} mb={4}>Reset {fullBagLabel(activeBag).toLowerCase()}?</Text>
                    <Text size="xs" c="dimmed" mb="xs">This puts all {bagChecked} checked pieces of this bag back.</Text>
                    <Group gap="xs" justify="flex-end">
                      <Button size="compact-xs" variant="default" onClick={() => setConfirmBagResetOpen(false)}>Cancel</Button>
                      <Button size="compact-xs" color="red" onClick={() => { setConfirmBagResetOpen(false); handleResetBag(activeBag.bagNumber); }}>Reset</Button>
                    </Group>
                  </Popover.Dropdown>
                </Popover>
              )}
              <SegmentedControl
                size="xs" radius="xl"
                aria-label="Order parts by"
                value={partOrder}
                onChange={changePartOrder}
                data={[
                  { value: 'color', label: 'Color' },
                  { value: 'type', label: 'Part type' },
                ]}
                style={{ marginLeft: 'auto', alignSelf: 'center', flexShrink: 0 }}
              />
            </div>

            {activeBag.leftover && !bagComplete && (
              <Box p="sm" mb="md" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 10 }}>
                <Text size="sm" c="dimmed">
                  <Text component="span" fw={600} c="dark">Checking this pile is optional.</Text>{' '}
                  These pieces aren&apos;t used by this build. Compare them with your remaining
                  pile to make sure no pieces are missing.
                </Text>
              </Box>
            )}
            {bagComplete ? (
              <Box p="xl" mb="md" style={{ textAlign: 'center', backgroundColor: 'var(--sort-good-soft)', borderRadius: 14 }} aria-live="polite">
                <IconCheck size={32} color="var(--sort-good)" style={{ marginBottom: 8 }} />
                <Text fw={700} size="lg" style={{ color: 'var(--sort-good)' }}>{fullBagLabel(activeBag)} complete!</Text>
                {nextUnfinishedIndex !== null && (
                  <Button
                    mt="md" color="green" radius="xl"
                    rightSection={<IconArrowRight size={16} />}
                    onClick={() => goToBag(nextUnfinishedIndex)}
                  >
                    {(() => {
                      const label = fullBagLabel(bags[nextUnfinishedIndex]);
                      return `Continue with ${label.charAt(0).toLowerCase()}${label.slice(1)}`;
                    })()}
                  </Button>
                )}
              </Box>
            ) : (
              <div className="sort-grid">
                {(() => {
                  const renderTile = (part) => (
                    <SortPartTile
                      key={`${part.partId}-${part.color_id || 'nocolor'}`}
                      part={part}
                      bagId={activeBag.id}
                      checkedCount={getCount(activeBag, part)}
                      onToggleCheck={handleToggleCheck}
                      isHighlighted={highlightedPartKey === `${part.partId}_${part.color_id || 'nocolor'}`}
                    />
                  );
                  if (!uncheckedGroups) return uncheckedParts.map(renderTile);
                  return uncheckedGroups.map((group, gi) => (
                    <Fragment key={group.key}>
                      <div className={`sort-typehead${gi === 0 ? ' first' : ''}`} role="heading" aria-level={2}>
                        <span>{group.label}</span>
                        <small>{group.parts.length} to find</small>
                      </div>
                      {group.parts.map(renderTile)}
                    </Fragment>
                  ));
                })()}
              </div>
            )}

            {checkedList.length > 0 && (
              <Box mt="md">
                <Box
                  onClick={() => setFoundOpen(o => !o)}
                  role="button" tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFoundOpen(o => !o); } }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
                    backgroundColor: 'var(--sort-good-soft)', borderRadius: 10, cursor: 'pointer',
                  }}
                >
                  <IconChevronDown size={18} style={{ transform: foundOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease', color: 'var(--sort-good)' }} />
                  <IconCheck size={18} color="var(--sort-good)" />
                  <Text fw={600} size="sm" style={{ color: 'var(--sort-good)' }}>Found ({fullyFoundCount})</Text>
                  {checkedList.length > fullyFoundCount && (
                    <Text size="xs" c="dimmed">+{checkedList.length - fullyFoundCount} partial</Text>
                  )}
                </Box>
                <Collapse in={foundOpen}>
                  <div className="sort-grid" style={{ marginTop: 10 }}>
                    {foundParts.map(part => (
                      <SortPartTile
                        key={`found-${part.partId}-${part.color_id || 'nocolor'}`}
                        part={part}
                        bagId={activeBag.id}
                        checkedCount={getCount(activeBag, part)}
                        inFoundSection
                        onToggleCheck={handleToggleCheck}
                        isHighlighted={highlightedPartKey === `${part.partId}_${part.color_id || 'nocolor'}`}
                      />
                    ))}
                  </div>
                </Collapse>
              </Box>
            )}
          </>
        )}
      </main>

      <footer className="sort-bottom">
        <Button
          variant="light" color="gray" radius="xl" style={{ flex: 1 }}
          disabled={!prevBag}
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => prevBag && goToBag(activeIndex - 1)}
          aria-label={prevBag ? `Previous: ${fullBagLabel(prevBag)}` : 'No previous bag'}
        >
          {prevBag ? shortBagLabel(prevBag) : '-'}
        </Button>
        <Button
          radius="xl" style={{ flex: 1.3 }}
          leftSection={<IconMoneybag size={16} />}
          onClick={() => setPickerOpen(true)}
        >
          {bags.length > 0 && bags.every(b => b.virtual) ? 'Groups' : 'Bags'}
        </Button>
        <Button
          variant="light" color="gray" radius="xl" style={{ flex: 1 }}
          disabled={!nextBag}
          rightSection={<IconArrowRight size={16} />}
          onClick={() => nextBag && goToBag(activeIndex + 1)}
          aria-label={nextBag ? `Next: ${fullBagLabel(nextBag)}` : 'No next bag'}
        >
          {nextBag ? shortBagLabel(nextBag) : '-'}
        </Button>
      </footer>

      <BagPickerSheet
        opened={pickerOpen}
        onClose={() => setPickerOpen(false)}
        bags={bags}
        bagCompletion={bagCompletion}
        activeIndex={activeIndex}
        onSelect={goToBag}
        shortLabel={shortBagLabel}
        totalChecked={totalCheckProgress.checked}
        onResetAll={handleResetAll}
      />

      <SortSearchSheet
        opened={searchOpen}
        onClose={() => setSearchOpen(false)}
        bags={bags}
        chipLabel={fullBagLabel}
        onJumpToPart={jumpToPart}
      />

      <MissingPartsSheet
        opened={reportOpen}
        onClose={() => setReportOpen(false)}
        setId={id}
        set={set}
        checkedParts={checkedParts}
        totalProgress={totalCheckProgress}
      />
    </div>
  );
}
